/**
 * Strava, from the app's side (`D282`): the four calls, and the export that
 * posts each finished workout while this device knows the account is
 * connected.
 *
 * The Worker holds the client secret and the tokens; the app only opens
 * Strava's authorisation page in the system browser and asks the Worker
 * what came of it. While the Worker has no Strava configured,
 * `GET /me/strava` answers `available: false` and nothing here is drawn or
 * sent (`ConnectionsCard`).
 *
 * ── WHY A DEVICE FLAG ──────────────────────────────────────────────────────
 * The export runs when a workout ends, often offline, and must not cost a
 * request per ended session to learn that Strava is off.
 * `STRAVA_CONNECTED_KEY` mirrors the last status the row read; a post the
 * Worker refuses with `strava.not_connected` clears it. The last posted
 * workout id is kept beside it, so a session is posted once per device.
 */
import { ApiError } from "@repo/client/api";
import {
  type AuthorizedFetcher,
  getJson,
  sendJson,
  sendNoContent,
} from "@repo/client/data/core";
import type { KeyValueStorage } from "@repo/client/i18n";
import {
  STRAVA_ROUTES,
  type StravaStatusOutput,
  stravaActivityOutput,
  stravaConnectOutput,
  stravaStatusOutput,
} from "@repo/contract";
import type { WorkoutSession } from "../workout/store/session-model";
import { healthWorkoutOf } from "./health-export";

/** `"on"` while this device last saw the account connected. */
export const STRAVA_CONNECTED_KEY = "mypocketgym.strava.connected.v1";
/** The id of the last workout posted from this device. */
export const STRAVA_LAST_KEY = "mypocketgym.strava.last.v1";

export function fetchStravaStatus(
  api: AuthorizedFetcher,
  signal?: AbortSignal
): Promise<StravaStatusOutput> {
  return getJson(
    api,
    STRAVA_ROUTES.status,
    stravaStatusOutput,
    signal === undefined ? undefined : { signal }
  );
}

/** Strava's authorisation address, to open in the system browser. */
export async function stravaConnectUrl(
  api: AuthorizedFetcher
): Promise<string> {
  const { authorizeUrl } = await sendJson(
    api,
    "POST",
    STRAVA_ROUTES.connect,
    undefined,
    stravaConnectOutput
  );
  return authorizeUrl;
}

export function disconnectStrava(api: AuthorizedFetcher): Promise<void> {
  return sendNoContent(api, "DELETE", STRAVA_ROUTES.status);
}

export interface StravaExportDeps {
  readonly post: (workout: {
    readonly endedAt: number;
    readonly id: string;
    readonly name: string;
    readonly startedAt: number;
  }) => Promise<unknown>;
  readonly storage: Pick<KeyValueStorage, "getItem" | "removeItem" | "setItem">;
  readonly titleOf: (session: WorkoutSession) => string;
}

/** Posts `POST /me/strava/activities` for one finished workout. */
export function postStravaWorkout(
  api: AuthorizedFetcher,
  workout: Parameters<StravaExportDeps["post"]>[0]
): Promise<unknown> {
  return sendJson(
    api,
    "POST",
    STRAVA_ROUTES.activities,
    {
      endedAt: new Date(workout.endedAt).toISOString(),
      name: workout.name,
      sessionId: workout.id,
      startedAt: new Date(workout.startedAt).toISOString(),
      utcOffsetMinutes: -new Date(workout.startedAt).getTimezoneOffset(),
    },
    stravaActivityOutput
  );
}

/** Whether a refusal says the account is no longer connected. */
export function endsConnection(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.messageKey === "strava.not_connected" ||
      error.messageKey === "strava.unavailable")
  );
}

export function createStravaExport(deps: StravaExportDeps) {
  const seen = new Set<string>();
  return {
    async update(session: WorkoutSession | null): Promise<void> {
      if (session === null || seen.has(session.sessionId)) {
        return;
      }
      const workout = healthWorkoutOf(session);
      if (workout === null) {
        return;
      }
      seen.add(workout.id);
      const [connected, last] = await Promise.all([
        deps.storage.getItem(STRAVA_CONNECTED_KEY),
        deps.storage.getItem(STRAVA_LAST_KEY),
      ]);
      if (connected !== "on" || last === workout.id) {
        return;
      }
      try {
        await deps.post({ ...workout, name: deps.titleOf(session) });
        await deps.storage.setItem(STRAVA_LAST_KEY, workout.id);
      } catch (error) {
        if (endsConnection(error)) {
          await deps.storage.removeItem(STRAVA_CONNECTED_KEY);
        }
      }
    },
  };
}
